import * as React from 'react'
import { styled, css } from 'styled-components'
import { Accordion } from 'react-bootstrap'
import * as domain from '@packages/domain'
import { pipe, RA, O, t, E } from '@packages/prelude'
import { Image } from '../Image'
import { TitleWithOptions } from './TitleWithOptions'
import { react } from '~/commons'

// Styled components
const StepContainer = styled(Accordion.Item)<{ $isActive: boolean }>`
  margin-bottom: 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;

  scroll-margin-top: 2rem;

  @media screen and (min-width: 768px) {
    scroll-margin-top: 4rem;
  }

  ${props => props.$isActive && 'border-color: transparent;'}
`

const StyledAccordionHeader = styled(Accordion.Header)<{ $isActive: boolean }>`
  ${props =>
    props.$isActive
      ? css`
          .accordion-button {
            background-color: var(--bs-primary);
            color: white;
          }
          ${StepTitle} {
            color: white;
          }
        `
      : css`
          .accordion-button {
            background-color: var(--bs-white);
          }
        `}

  .accordion-button {
    display: flex;
    align-items: center;
  }
`

const StepTitle = styled.h4`
  text-transform: uppercase;
`

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const CircularImage = styled(Image)`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 15px;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const TitlePriceContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const PriceText = styled.div`
  font-size: 14px;
  color: #666;
  margin-top: 4px;
`

const Description = styled.div`
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;

  @media screen and (min-width: 992px) {
    max-width: 60%;
  }
`

const SelectionHint = styled(Description)`
  font-style: italic;
`

export interface StepProps {
  step: domain.Step.Type
  isActive: boolean
  children?: React.ReactNode
  eventKey: string
  selectedOptions?: ReadonlyArray<domain.Option.Type>
}

export const Step: React.FC<StepProps> = ({ step, isActive, children, eventKey, selectedOptions = [] }) => {
  const hasSelectedOptions = selectedOptions.length > 0

  const totalPrice = pipe(
    selectedOptions,
    RA.map(option => option.price),
    RA.reduce(0, (sum, price) => sum + price)
  )

  const showPreview = !isActive && hasSelectedOptions

  return (
    <StepContainer eventKey={eventKey} id={eventKey} $isActive={isActive}>
      <StyledAccordionHeader $isActive={isActive}>
        {showPreview ? (
          <HeaderContent>
            <TitlePriceContainer>
              <StepTitle>{step.title}</StepTitle>
              {totalPrice > 0 && (
                <PriceText>
                  {pipe(
                    selectedOptions,
                    RA.head,
                    O.foldMap(react.monoid)(firstOption => (
                      <>
                        <strong>
                          {firstOption.name}
                          {RA.size(selectedOptions) >= 2 ? ' und weitere' : ''}:
                        </strong>{' '}
                      </>
                    ))
                  )}
                  {totalPrice.toLocaleString('de-DE')} €
                </PriceText>
              )}
            </TitlePriceContainer>
            {pipe(
              selectedOptions,
              RA.head,
              O.foldMap(react.monoid)(firstOption => <CircularImage imageId={firstOption.image} size="thumbnail" />)
            )}
          </HeaderContent>
        ) : isActive ? (
          <StepTitle>{step.title}</StepTitle>
        ) : (
          <TitleWithOptions step={step} />
        )}
      </StyledAccordionHeader>
      <Accordion.Body className="py-0">
        <>
          {step.selectionMode === 'multiple' && <SelectionHint>Sie können mehrere Optionen auswählen:</SelectionHint>}
          {pipe(
            step.description,
            t.NonEmptyString.decode,
            E.foldMap(react.monoid)(description => <Description>{description}</Description>)
          )}
          {children}
        </>
      </Accordion.Body>
    </StepContainer>
  )
}
